import React from "react";
import {
  List,
  ListItem as ListItemBase,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Chip as ChipBase,
  Checkbox,
} from "@mui/material";
import { useFormikContext } from "formik";
import { getCreativePackageLabel } from "@/features/campaigns/digital/launch-digital-campaign/helpers/creativeLabel";

type CreativeOption = {
  id: number | string;
  package_name?: string;
  creative_name?: string;
  detail_1?: string;
  detail_2?: string;
  [key: string]: unknown;
};

type SelectedLocationValues = {
  extend_creatives?: Array<number | string>;
  creatives?: Array<{
    creative_id?: number | string;
  }>;
  can_update_creatives?: boolean;
};

type ExtendCreativeFormValues = {
  selectedLoc: Record<string | number, SelectedLocationValues | undefined>;
};

type ExtendCreativeOptionsProps = {
  creatives?: CreativeOption[];
  viewCreative?: number | string | null;
  handleSelectCreative?: (_creativeId: number | string) => void;
  onPreview?: (_creativeId: number | string) => void;
  location_id: number | string;
  creative_select_max_value?: number | string | null;
};

const ListItem = ListItemBase as React.ComponentType<Record<string, any>>;
const Chip = ChipBase as React.ComponentType<Record<string, any>>;

function ExtendCreativeOptions({
  creatives,
  viewCreative,
  handleSelectCreative,
  onPreview,
  location_id,
  creative_select_max_value,
}: ExtendCreativeOptionsProps) {
  const { values } = useFormikContext<ExtendCreativeFormValues>();

  const handleNameClick = (creativeId: number | string) => {
    if (onPreview) {
      onPreview(creativeId);
    }
  };

  const handleCheckboxClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    creativeId: number | string
  ) => {
    e.stopPropagation(); // Prevent triggering preview when clicking checkbox
    if (handleSelectCreative) {
      handleSelectCreative(creativeId);
    }
  };

  const selectedCount =
    values.selectedLoc[location_id]?.extend_creatives?.length || 0;
  const maxValue =
    creative_select_max_value === null ? 1 : Number(creative_select_max_value);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          Select up to {creative_select_max_value}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "var(--neutral-700)",
              fontWeight: 500,
            }}
          >
            Selected {selectedCount}/{maxValue}
          </Typography>
          <Box
            sx={{
              width: "1px",
              height: "20px",
              backgroundColor: "var(--primary-base)",
            }}
          />
        </Box>
      </Box>
      <Box
        sx={{
          position: "relative",
          maxHeight: "350px",
          overflowY: "auto",
        }}
      >
        <List
          sx={{
            width: "100%",
            bgcolor: "background.paper",
            paddingTop: 2,
          }}
          dense
        >
          {creatives?.map((creative) => {
            const creativePackageLabel = getCreativePackageLabel(creative);
            const isSelected = values.selectedLoc[
              location_id
            ]?.extend_creatives?.includes(creative.id);

            const isInView = viewCreative === creative.id;

            const isCurrentCreative = values.selectedLoc[
              location_id
            ]?.creatives?.find(
              (c) => Number(c.creative_id) === Number(creative.id)
            );

            const canUpdateCreatives =
              values.selectedLoc[location_id]?.can_update_creatives;

            return (
              <Box
                key={creative.id}
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  marginBottom: 1,
                  backgroundColor: isSelected
                    ? "var(--primary-tint)"
                    : "transparent",
                  borderRadius: "var(--radius-container)",
                  padding: "8px",
                  marginRight: 0.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: "36px",
                    borderRadius: "var(--radius-control)",
                  }}
                >
                  <Checkbox
                    edge="start"
                    checked={isSelected}
                    tabIndex={-1}
                    disableRipple
                    onClick={(e) => {
                      if (canUpdateCreatives) {
                        handleCheckboxClick(e, creative.id);
                      }
                    }}
                    disabled={!canUpdateCreatives}
                    inputProps={{
                      "aria-labelledby": `checkbox-list-label-${creative.id}`,
                    }}
                    sx={{
                      color: "var(--primary-base)",
                      "&.Mui-checked": {
                        color: "var(--primary-base)",
                      },
                    }}
                  />
                </ListItemIcon>
                {isCurrentCreative && (
                  <Chip
                    label="Current Creative"
                    sx={{
                      position: "absolute",
                      top: "-5px",
                      right: "26px",
                      fontSize: "8px",
                      backgroundColor: "var(--surface-dark)",
                      color: "var(--neutral-white)",
                      height: "15px",
                      borderTopLeftRadius: "var(--radius-round)",
                      borderTopRightRadius: "var(--radius-round)",
                      borderBottomRightRadius: "initial",
                      borderBottomLeftRadius: "initial",
                    }}
                    size="medium"
                    square
                  />
                )}
                <ListItem
                  dense
                  button
                  start
                  onClick={() => handleNameClick(creative.id)}
                  sx={{
                    backgroundColor: isSelected
                      ? "var(--primary-base)"
                      : isInView && !isSelected
                        ? "var(--primary-tint)"
                        : "var(--neutral-white)",
                    color: isSelected
                      ? "var(--neutral-white)"
                      : isInView && !isSelected
                        ? "var(--palette-text-primary)"
                        : "var(--primary-base)",
                    border:
                      (isInView && !isSelected) || isSelected
                        ? "1px solid var(--primary-base)"
                        : "1px solid var(--neutral-200)",
                    borderRadius: "var(--radius-control)",
                    paddingRight: 1,
                    marginRight: 1,
                    flex: 1,
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: isSelected
                        ? "var(--primary-base)"
                        : isInView && !isSelected
                          ? "var(--primary-tint)"
                          : "var(--neutral-white)",
                      color: isSelected
                        ? "var(--neutral-white)"
                        : isInView && !isSelected
                          ? "var(--palette-text-primary)"
                          : "var(--primary-base)",
                    },
                  }}
                >
                  <ListItemText
                    id={`checkbox-list-label-${creative.id}`}
                    primary={creativePackageLabel}
                    className="creative-option-label"
                    sx={{
                      fontWeight: isInView ? "bolder" : "normal",
                      textDecoration: "none",
                    }}
                  />
                </ListItem>
              </Box>
            );
          })}
        </List>
      </Box>
    </Box>
  );
}

export default ExtendCreativeOptions;
